<script setup lang="ts">
import { computed, ref } from 'vue'

import { useBetaDashboardStore } from '@/stores/beta-dashboard'
import type { BetaAccessPeriod } from '@/types/beta-dashboard'

const betaStore = useBetaDashboardStore()
const showAccessDetails = ref(false)
const accessPages = computed(() => betaStore.accessCount?.pages ?? [])
const pagination = computed(() => betaStore.accessCount?.pagination ?? {
    page: 1,
    perPage: 20,
    total: 0,
    lastPage: 1,
})
const periods: Array<{ days: BetaAccessPeriod; label: string }> = [
    { days: 1, label: 'Hoje' },
    { days: 15, label: '15 dias' },
    { days: 30, label: '30 dias' },
]

const formatNumber = (value: number) => new Intl.NumberFormat('pt-BR').format(value)
</script>

<template>
    <section class="beta-metrics">
        <div class="beta-metrics__heading">
            <div>
                <p>Desempenho</p>
                <h2>Seu site em números</h2>
            </div>
            <span>Atualização automática</span>
        </div>

        <div class="beta-metrics__access-toolbar">
            <span>Período</span>
            <div role="group" aria-label="Período das métricas">
                <button
                    v-for="period in periods"
                    :key="period.days"
                    type="button"
                    :class="{ 'is-active': betaStore.accessPeriod === period.days }"
                    :aria-pressed="betaStore.accessPeriod === period.days"
                    :disabled="betaStore.loadingAccessCount || betaStore.loadingOpportunitiesCount"
                    @click="betaStore.setAccessPeriod(period.days)"
                >
                    {{ period.label }}
                </button>
            </div>
        </div>

        <div
            class="beta-metrics__grid"
            :class="{ 'has-details': showAccessDetails }"
        >
            <article class="beta-metric-card beta-metric-card--access">
                <span>Acessos</span>
                <strong v-if="betaStore.loadingAccessCount" class="beta-metric-card__value-loading" />
                <strong v-else>
                    {{ betaStore.accessCountError ? '—' : formatNumber(betaStore.accessCount?.total ?? 0) }}
                </strong>
                <button
                    v-if="betaStore.accessCountError"
                    type="button"
                    class="beta-metric-card__retry"
                    @click="betaStore.loadAccessCount"
                >
                    Tentar novamente
                </button>
                <div class="beta-metric-card__footer">
                    <small v-if="!betaStore.accessCountError">Visualizações da página inicial</small>
                    <button
                        type="button"
                        class="beta-metric-card__details"
                        aria-controls="beta-page-access-details"
                        :aria-expanded="showAccessDetails"
                        @click="showAccessDetails = !showAccessDetails"
                    >
                        {{ showAccessDetails ? 'Fechar' : 'Detalhes' }}
                    </button>
                </div>
            </article>

            <Transition name="beta-page-access">
                <section
                    v-if="showAccessDetails"
                    id="beta-page-access-details"
                    class="beta-page-access"
                >
                    <header>
                        <div>
                            <span>Detalhamento</span>
                            <h3>Acessos por página</h3>
                        </div>
                        <small v-if="!betaStore.loadingAccessCount && !betaStore.accessCountError">
                            {{ formatNumber(pagination.total) }} página{{ pagination.total === 1 ? '' : 's' }} acessada{{ pagination.total === 1 ? '' : 's' }}
                        </small>
                    </header>

                    <div v-if="betaStore.loadingAccessCount" class="beta-page-access__loading" aria-label="Carregando acessos por página">
                        <span v-for="index in 4" :key="index" />
                    </div>

                    <div v-else-if="betaStore.accessCountError" class="beta-page-access__message beta-page-access__message--error">
                        <div>
                            <strong>Não foi possível carregar os acessos por página.</strong>
                            <p>{{ betaStore.accessCountError }}</p>
                        </div>
                        <button type="button" @click="betaStore.loadAccessCount">Tentar novamente</button>
                    </div>

                    <div v-else-if="!accessPages.length" class="beta-page-access__message">
                        <strong>Nenhum acesso registrado neste período.</strong>
                        <p>As páginas aparecerão aqui depois que o site receber visitas.</p>
                    </div>

                    <template v-else>
                        <ol class="beta-page-access__list">
                            <li v-for="(page, index) in accessPages" :key="page.pageId">
                                <span class="beta-page-access__position" aria-hidden="true">
                                    {{ ((pagination.page - 1) * pagination.perPage) + index + 1 }}.
                                </span>
                                <div>
                                    <strong>{{ page.title || `Página #${page.pageId}` }}</strong>
                                </div>
                                <span class="beta-page-access__count">
                                    <strong>{{ formatNumber(page.accessCount) }}</strong>
                                    <small>acesso{{ page.accessCount === 1 ? '' : 's' }}</small>
                                </span>
                            </li>
                        </ol>

                        <nav v-if="pagination.lastPage > 1" class="beta-page-access__pagination" aria-label="Paginação dos acessos por página">
                            <button
                                type="button"
                                :disabled="pagination.page <= 1 || betaStore.loadingAccessCount"
                                @click="betaStore.setAccessPage(pagination.page - 1)"
                            >
                                Anterior
                            </button>
                            <span>Página {{ pagination.page }} de {{ pagination.lastPage }}</span>
                            <button
                                type="button"
                                :disabled="pagination.page >= pagination.lastPage || betaStore.loadingAccessCount"
                                @click="betaStore.setAccessPage(pagination.page + 1)"
                            >
                                Próxima
                            </button>
                        </nav>
                    </template>
                </section>
            </Transition>

            <article class="beta-metric-card beta-metric-card--opportunities">
                <span>Oportunidades</span>
                <strong v-if="betaStore.loadingOpportunitiesCount" class="beta-metric-card__value-loading" />
                <strong v-else>
                    {{ betaStore.opportunitiesCountError ? '—' : formatNumber(betaStore.opportunitiesCount) }}
                </strong>
                <button
                    v-if="betaStore.opportunitiesCountError"
                    type="button"
                    class="beta-metric-card__retry"
                    @click="betaStore.loadOpportunitiesCount"
                >
                    Tentar novamente
                </button>
                <small v-else>Contatos recebidos no período</small>
            </article>
        </div>
    </section>
</template>

<style scoped>
.beta-metrics {
    display: grid;
    gap: 17px;
}

.beta-metrics__heading,
.beta-metrics__access-toolbar,
.beta-page-access > header,
.beta-page-access__pagination {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 20px;
}

.beta-metrics__heading {
    align-items: end;
}

.beta-metrics__heading p,
.beta-page-access header div > span {
    color: var(--beta-accent, var(--main-color, #2f86f6));
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.18em;
    text-transform: uppercase;
}

.beta-metrics__heading h2,
.beta-page-access h3 {
    margin-top: 7px;
    color: #172132;
    font-family: 'Outfit', ui-sans-serif, system-ui, sans-serif;
    font-size: 18px;
    font-weight: 600;
    letter-spacing: -0.02em;
}

.beta-metrics__heading > span,
.beta-metrics__access-toolbar > span,
.beta-page-access header > small {
    color: #8b9ab0;
    font-size: 12px;
    font-weight: 700;
}

.beta-metrics__access-toolbar {
    justify-content: flex-end;
}

.beta-metrics__access-toolbar > div {
    display: inline-flex;
    gap: 4px;
    border: 1px solid #dde5ef;
    border-radius: 14px;
    background: #f7f9fc;
    padding: 4px;
}

.beta-metrics__access-toolbar button {
    min-height: 31px;
    border-radius: 10px;
    padding: 0 12px;
    color: #71829a;
    font-size: 12px;
    font-weight: 800;
}

.beta-metrics__access-toolbar button.is-active {
    background: #fff;
    box-shadow: 0 3px 10px rgb(29 52 84 / 10%);
    color: var(--main-color);
}

.beta-metrics__access-toolbar button:disabled {
    cursor: wait;
    opacity: 0.65;
}

.beta-metrics__grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    column-gap: 14px;
    row-gap: 17px;
}

.beta-metric-card--access {
    grid-column: 1;
    grid-row: 1;
}

.beta-metric-card--opportunities {
    grid-column: 2;
    grid-row: 1;
}

.beta-metric-card {
    display: grid;
    min-height: 132px;
    align-content: space-between;
    border: 1px solid #e0e7f1;
    border-radius: 22px;
    background: #fff;
    padding: 20px;
    text-align: left;
}

.beta-metric-card span {
    color: #7d8ea8;
    font-size: 14px;
    font-weight: 800;
}

.beta-metric-card > strong {
    color: #10233e;
    font-size: 28px;
    font-weight: 900;
}

.beta-metric-card small {
    color: #91a0b4;
    font-size: 11px;
}

.beta-metric-card__value-loading {
    display: block;
    width: 72px;
    height: 34px;
    border-radius: 9px;
    background: linear-gradient(90deg, #edf1f6, #f8fafc, #edf1f6);
    background-size: 200% 100%;
    animation: beta-metrics-loading 1.4s linear infinite;
}

.beta-metric-card__retry {
    justify-self: start;
    color: var(--main-color);
    font-size: 12px;
    font-weight: 700;
    text-decoration: underline;
}

.beta-metric-card__footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
}

.beta-metric-card__details {
    margin-left: auto;
    border-radius: 9px;
    background: color-mix(in srgb, var(--main-color) 10%, white);
    padding: 7px 10px;
    color: var(--main-color);
    font-size: 12px;
    font-weight: 800;
    transition: background-color 180ms ease, transform 180ms ease;
}

.beta-metric-card__details:hover,
.beta-metric-card__details[aria-expanded='true'] {
    background: color-mix(in srgb, var(--main-color) 16%, white);
}

.beta-metric-card__details:active {
    transform: translateY(1px);
}

.beta-page-access {
    display: grid;
    grid-column: 1 / -1;
    grid-row: 2;
    gap: 16px;
    border: 1px solid #e0e7f1;
    border-radius: 22px;
    background: #fff;
    padding: 20px;
}

.beta-page-access__list {
    display: grid;
    margin: 0;
    padding: 0;
    list-style: none;
}

.beta-page-access__list li {
    display: grid;
    grid-template-columns: 24px minmax(0, 1fr) auto;
    align-items: center;
    gap: 8px;
    min-height: 44px;
    border-top: 1px solid #edf1f6;
}

.beta-page-access__list li:first-child {
    border-top: 0;
}

.beta-page-access__position {
    color: #9aa8ba;
    font-size: 12px;
    font-weight: 800;
}

.beta-page-access__list li > div {
    min-width: 0;
}

.beta-page-access__list li > div strong {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: #21334d;
    font-size: 14px;
}

.beta-page-access__count {
    display: grid;
    justify-items: end;
}

.beta-page-access__count strong {
    color: #10233e;
    font-size: 14px;
}

.beta-page-access__count small {
    color: #8c9aae;
    font-size: 12px;
}

.beta-page-access__loading {
    display: grid;
    gap: 9px;
}

.beta-page-access__loading span {
    height: 52px;
    border-radius: 12px;
    background: linear-gradient(90deg, #edf1f6, #f8fafc, #edf1f6);
    background-size: 200% 100%;
    animation: beta-metrics-loading 1.4s linear infinite;
}

.beta-page-access__message {
    border-radius: 14px;
    background: #f7f9fc;
    padding: 18px;
    color: #687990;
    font-size: 14px;
}

.beta-page-access__message p {
    margin-top: 4px;
    font-size: 11px;
}

.beta-page-access__message--error {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 14px;
    background: #fff7f7;
    color: #934444;
}

.beta-page-access__message button,
.beta-page-access__pagination button {
    border-radius: 10px;
    background: color-mix(in srgb, var(--main-color) 10%, white);
    padding: 8px 11px;
    color: var(--main-color);
    font-size: 12px;
    font-weight: 800;
}

.beta-page-access__pagination {
    justify-content: center;
}

.beta-page-access__pagination span {
    color: #74849a;
    font-size: 12px;
    font-weight: 700;
}

.beta-page-access__pagination button:disabled {
    cursor: not-allowed;
    opacity: 0.45;
}

.beta-page-access-enter-active,
.beta-page-access-leave-active {
    transform-origin: top;
    transition: opacity 240ms ease, transform 240ms ease;
}

.beta-page-access-enter-from,
.beta-page-access-leave-to {
    opacity: 0;
    transform: translateY(-8px) scaleY(0.98);
}

@keyframes beta-metrics-loading {
    to { background-position: -200% 0; }
}

@media (max-width: 767px) {
    .beta-metrics__heading,
    .beta-metrics__access-toolbar,
    .beta-page-access > header {
        align-items: start;
        flex-direction: column;
    }

    .beta-metrics__access-toolbar {
        align-items: stretch;
    }

    .beta-metrics__access-toolbar > div {
        width: 100%;
    }

    .beta-metrics__access-toolbar button {
        flex: 1;
    }

    .beta-metrics__grid {
        grid-template-columns: 1fr;
    }

    .beta-metric-card--access {
        grid-column: 1;
        grid-row: 1;
    }

    .beta-metric-card--opportunities {
        grid-column: 1;
        grid-row: 2;
    }

    .beta-page-access {
        grid-column: 1;
        grid-row: 2;
    }

    .beta-metrics__grid.has-details .beta-metric-card--opportunities {
        grid-row: 3;
    }

    .beta-page-access__message--error {
        align-items: flex-start;
        flex-direction: column;
    }
}
</style>
